import {
    RadialBarChart,
    RadialBar,
    PolarAngleAxis,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
} from "recharts";
import { useI18n } from "@/i18n/I18nProvider";

function GaugeCard({ label, value, verdict, color = "#D4FF00", testId, proprietary }) {
    return (
        <div
            data-testid={testId}
            className="card-hover relative rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-7 reveal"
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00]">
                    {label}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-white/40 font-mono">
                    {proprietary}
                </span>
            </div>
            <div className="relative h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                        cx="50%"
                        cy="62%"
                        innerRadius="76%"
                        outerRadius="100%"
                        barSize={14}
                        startAngle={180}
                        endAngle={0}
                        data={[{ name: label, value, fill: color }]}
                    >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                            background={{ fill: "#0E2B1E" }}
                            dataKey="value"
                            cornerRadius={20}
                        />
                    </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-4 pointer-events-none">
                    <div className="font-display text-4xl font-bold tabular text-white">
                        {value}
                        <span style={{ color }}>%</span>
                    </div>
                </div>
            </div>
            <div className="mt-3 text-xs text-white/70 leading-snug min-h-[40px]">
                {verdict}
            </div>
        </div>
    );
}

const pieData = [
    { name: "Player", value: 51.25, fill: "#D4FF00" },
    { name: "Opponents", value: 48.75, fill: "#1A4030" },
];

const rallyData = [
    { bucket: "0", val: 49 },
    { bucket: "1/2", val: 57 },
    { bucket: "3/4", val: 51 },
    { bucket: "5/6", val: 57 },
    { bucket: "7/8", val: 60 },
    { bucket: "9/10", val: 50 },
    { bucket: "11/12", val: 44 },
    { bucket: "13/14", val: 41 },
    { bucket: "15+", val: 38 },
];

export default function Metrics() {
    const { t } = useI18n();
    const m = t.metrics;
    return (
        <section
            id="metrics"
            data-testid="metrics-section"
            className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
        >
            <div
                className="absolute left-1/2 -translate-x-1/2 top-0 w-[1100px] h-[700px] pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(212,255,0,0.07), transparent 70%)",
                }}
                aria-hidden
            />
            <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-8 mb-14 reveal">
                    <div className="lg:col-span-7">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-4">
                            {m.tag}
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter heading-clamp">
                            {m.headline}{" "}
                            <span className="text-[#D4FF00]">{m.headlineAccent}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 self-end">
                        <p className="text-white/65 text-lg leading-relaxed">{m.sub}</p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <GaugeCard
                        testId="metric-sem"
                        label="S.E.M."
                        value={57.54}
                        verdict={m.semVerdict}
                        proprietary={m.proprietary}
                    />
                    <GaugeCard
                        testId="metric-rem"
                        label="R.E.M."
                        value={41.89}
                        verdict={m.remVerdict}
                        proprietary={m.proprietary}
                    />
                    <GaugeCard
                        testId="metric-p1"
                        label={m.p1Heading}
                        value={63.12}
                        color="#FF3B30"
                        verdict={m.p1Verdict}
                        proprietary={m.proprietary}
                    />

                    <div
                        data-testid="metric-leadership"
                        className="card-hover rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-7 reveal"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00]">
                                {m.leadershipLabel}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-white/40 font-mono">
                                {m.leadershipCaption}
                            </span>
                        </div>
                        <div className="relative h-[180px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        innerRadius={55}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {pieData.map((d, i) => (
                                            <Cell key={i} fill={d.fill} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <div className="font-display text-3xl font-bold tabular text-white">
                                    51.25<span className="text-[#D4FF00]">%</span>
                                </div>
                                <div className="text-[10px] text-white/45 uppercase tracking-widest mt-1">
                                    {m.leadershipPointsLabel}
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 text-xs text-white/70 leading-snug">
                            {m.leadershipBody}
                        </div>
                    </div>

                    <div
                        data-testid="metric-rally"
                        className="card-hover rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-7 reveal lg:col-span-2"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00]">
                                {m.rallyLabel}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-white/40 font-mono">
                                {m.rallyCaption}
                            </span>
                        </div>
                        <div className="flex items-end gap-6">
                            <div className="font-display text-6xl md:text-7xl font-bold tabular text-white leading-none">
                                57<span className="text-[#D4FF00]">%</span>
                            </div>
                            <div className="flex-1 h-[140px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={rallyData}>
                                        <XAxis
                                            dataKey="bucket"
                                            tick={{
                                                fill: "rgba(255,255,255,0.45)",
                                                fontSize: 10,
                                            }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Bar
                                            dataKey="val"
                                            radius={[6, 6, 0, 0]}
                                            fill="#D4FF00"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="mt-3 text-xs text-white/70 leading-snug">
                            {m.rallyBody}
                        </div>
                    </div>
                </div>

                <div
                    data-testid="simulation-engine"
                    className="card-hover rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-8 mt-5 reveal"
                >
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                        <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00]">
                            {m.simTag}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-white/40 font-mono">
                            {m.simChip}
                        </span>
                    </div>
                    <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight mb-4">
                        {m.simTitle}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                        <p className="text-[15px] text-white/75 leading-relaxed">{m.simBody}</p>
                        <p className="text-[15px] text-white/65 leading-relaxed border-l-2 border-[#D4FF00]/40 pl-4">
                            {m.simCase}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
